import RemoveIcon from "@mui/icons-material/CloseRounded";
import {
  FormHelperText,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FilesDropArea from "@entrilia/shared/components/FilesDropArea";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { formatFileSize } from "@entrilia/shared/utilities/fileHelper";
import { formatPercentage } from "@entrilia/shared/utilities/formatters";
import InvestorCommunicationTypeAvatar from "../../InvestorCommunicationTypeAvatar";
import InvestorCommunicationContext from "../InvestorCommunicationContext";
import type { VideoEditState } from "../investorCommunicationVideoEditState";
import { acceptedVideoFileExtensions, isAcceptedVideoFile, maxVideoFileSizeBytes } from "./investorCommunicationFiles";

interface Props {
  videoState: VideoEditState;
}

const InvestorCommunicationVideoFileField = ({ videoState }: Props) => {
  const { state, isSaving, videoUploadProgress, validationErrors, onVideoFileAdd, onVideoFileRemove } =
    useDefinedContext(InvestorCommunicationContext, "InvestorCommunicationContext");

  const { sendNotificationError } = useNotificationContext();

  const handleFilesAdd = (files: File[]) => {
    const file = files[0];
    if (file === undefined) {
      return;
    }

    if (!isAcceptedVideoFile(file)) {
      sendNotificationError(`Unsupported video format. Allowed: ${acceptedVideoFileExtensions.join(", ")}`);
      return;
    }

    if (file.size > maxVideoFileSizeBytes) {
      sendNotificationError("The video is too large. Maximum size is 1 GB.");
      return;
    }

    onVideoFileAdd(file);
  };

  const { videoFile, isVideoRemoved } = videoState;
  const savedVideoContent = isVideoRemoved ? undefined : state.savedCommunication.videoContent;
  const fileName = videoFile?.name ?? savedVideoContent?.fileName;
  const fileSize = videoFile?.size ?? savedVideoContent?.fileSize;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">
        Video <RequiredFieldMarker />
      </Typography>

      {fileName ? (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell sx={{ width: 120 }}>Size</TableCell>
                <TableCell sx={{ width: 64 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0 }}>
                    <InvestorCommunicationTypeAvatar type="Video" size={24} />
                    <Typography noWrap>{fileName}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{fileSize === undefined ? "" : formatFileSize(fileSize)}</TableCell>
                <TableCell align="right">
                  <IconButton aria-label="Remove video" disabled={isSaving} onClick={onVideoFileRemove}>
                    <RemoveIcon color={isSaving ? "disabled" : "error"} />
                  </IconButton>
                </TableCell>
              </TableRow>
              {videoUploadProgress !== undefined && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <LinearProgress variant="determinate" value={videoUploadProgress * 100} sx={{ flex: 1 }} />
                      <Typography color="textSecondary">{formatPercentage(videoUploadProgress)}</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <FilesDropArea
          acceptedFileExtensions={acceptedVideoFileExtensions}
          subtitle="16:9 recommended. MP4, MOV, WEBM or OGV, up to 1 GB."
          disabled={isSaving}
          onFilesAdd={handleFilesAdd}
        />
      )}

      {validationErrors.video && <FormHelperText error>{validationErrors.video}</FormHelperText>}
    </Stack>
  );
};

export default InvestorCommunicationVideoFileField;
